import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import image from "../../images/javian-headshot.jpg";
import "./about.css";

const SECTIONS = [
  { id: "about", number: "01", label: "About" },
  { id: "certifications", number: "02", label: "Certifications" },
  { id: "security-domains", number: "03", label: "Security Domains" },
  { id: "tooling", number: "04", label: "Tooling" },
];

const CERTS = [
  {
    name: "Certified Information Security Manager (CISM)",
    issuer: "ISACA",
    date: "2026",
    description:
      "Covers information security governance, risk management, program development, and incident management for security leadership roles.",
  },
  {
    name: "Certified Digital Forensics Examiner (CDFE)",
    issuer: "Mile2",
    date: "2025",
    description:
      "Forensic artifact collection, evidence handling, and investigative methodology for digital forensics examinations.",
  },
  {
    name: "Certified in Cybersecurity (CC)",
    issuer: "ISC2",
    date: "2024",
    description:
      "Foundational cybersecurity principles spanning security operations, incident response, access control, and network security.",
  },
  {
    name: "AWS Certified Cloud Practitioner",
    issuer: "Amazon Web Services",
    date: "2023",
    description:
      "Foundational certification covering AWS Cloud concepts, core services, billing and pricing, and security and compliance basics.",
  },
  {
    name: "CompTIA Network+",
    issuer: "CompTIA",
    date: "2022",
    description:
      "Vendor-neutral certification covering network infrastructure, operations, security, and troubleshooting across wired and wireless environments.",
  },
  {
    name: "ITIL 4 Foundation",
    issuer: "PeopleCert",
    date: "2022",
    description:
      "Foundational certification in IT service management, covering the ITIL 4 service value system and key ITSM practices.",
  },
  {
    name: "Google IT Support Professional Certificate",
    issuer: "Google",
    date: "2021",
    description:
      "Five-course, hands-on program covering troubleshooting, customer service, networking, operating systems, system administration, and security fundamentals for entry-level IT support roles.",
  },
];

const SECURITY_DOMAINS = [
  {
    name: "Digital Forensics",
    detail: "Forensic artifact collection, evidence handling, and analysis with FTK Imager and Autopsy.",
  },
  {
    name: "Email Security",
    detail: "Proofpoint, security awareness training, phishing simulations, DMARC/DKIM/SPF.",
  },
  {
    name: "Endpoint Protection & Posture",
    detail: "EDR operations, hardening, vulnerability and patch coordination.",
  },
  {
    name: "Identity & Access",
    detail: "SSO, role design, privileged access, audit readiness.",
  },
  {
    name: "Digital Risk & eDiscovery",
    detail: "Brand and domain protection, investigations, reporting.",
  },
];

const TOOLING = [
  "Proofpoint",
  "SentinelOne",
  "Splunk",
  "OneLogin",
  "FTK Imager",
  "Autopsy",
  "Tenable",
];

function useScrollSpy(sectionIds) {
  const [activeId, setActiveId] = useState(sectionIds[0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-120px 0px -70% 0px", threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionIds]);

  return activeId;
}

function About() {
  const sectionIds = useRef(SECTIONS.map((section) => section.id)).current;
  const activeId = useScrollSpy(sectionIds);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && sectionIds.includes(hash)) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ block: "start" }), 0);
      }
    }
  }, [sectionIds]);

  const handleNavClick = (event, id) => {
    event.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  return (
    <div className="about-minimal">
      <div className="about-minimal__layout">
        <nav className="about-minimal__nav" aria-label="About page sections">
          {SECTIONS.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={`about-minimal__nav-link${
                activeId === section.id ? " is-active" : ""
              }`}
              onClick={(event) => handleNavClick(event, section.id)}
            >
              <span>{section.number}.</span> {section.label}
            </a>
          ))}
        </nav>

        <div className="about-minimal__content">
          <section id="about" className="about-minimal__section">
            <p className="about-minimal__eyebrow">01. About</p>
            <h1 className="about-minimal__heading">Javian Anderson</h1>
            <p className="about-minimal__role">
              Digital Forensics and Cybersecurity Professional
            </p>
            <div className="about-minimal__intro">
              <div className="about-minimal__photo">
                <img src={image} alt="Javian Anderson" />
              </div>
              <div className="about-minimal__text">
                <p>
                  I'm a security professional who started in security
                  operations and now leads email security and identity for
                  Digicel Group, a telecom headquartered in Kingston,
                  Jamaica that spans 24 markets across the Caribbean and
                  Central America. Along the way I've picked up hands-on
                  forensic work: artifact
                  extraction, evidence handling, and incident timelines,
                  which is what pulled me toward a graduate program in
                  digital forensics at John Jay College (CUNY).
                </p>
                <p>
                  I'm from Portland, Jamaica, and I've spent a fair amount of
                  my life in leadership and service roles, from student
                  government to my current work leading security awareness
                  training for thousands of employees. My long-term goal is
                  to help Jamaica rank among the world's safest digital
                  markets.
                </p>
                <p className="about-minimal__quote">
                  "Keep moving forward." Lewis, Meet the Robinsons, 2007
                </p>
                <Link
                  to="/files/JAVIAN_ANDERSON_RESUME.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button type="button" className="about-minimal__btn">
                    Download Resume
                  </button>
                </Link>
              </div>
            </div>
          </section>

          <section id="certifications" className="about-minimal__section">
            <p className="about-minimal__eyebrow">02. Certifications</p>
            <h2 className="about-minimal__heading">Certifications</h2>
            <a
              href="https://www.credly.com/users/javian-anderson.f13701c6/badges/credly"
              target="_blank"
              rel="noopener noreferrer"
              className="about-minimal__credly-link"
            >
              View verified badges on Credly
              <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
            </a>
            <div className="about-minimal__cert-list">
              {CERTS.map((cert) => (
                <div className="about-minimal__cert" key={cert.name}>
                  <div className="about-minimal__cert-date">{cert.date}</div>
                  <div className="about-minimal__cert-body">
                    <h3>{cert.name}</h3>
                    <p className="about-minimal__cert-issuer">{cert.issuer}</p>
                    <p>{cert.description}</p>
                    {cert.badge && (
                      <span className="about-minimal__cert-badge">
                        {cert.badge}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="security-domains" className="about-minimal__section">
            <p className="about-minimal__eyebrow">03. Security Domains</p>
            <h2 className="about-minimal__heading">Security Domains</h2>
            <div className="about-minimal__domain-list">
              {SECURITY_DOMAINS.map((domain) => (
                <div className="about-minimal__domain" key={domain.name}>
                  <h3>{domain.name}</h3>
                  <p>{domain.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="tooling" className="about-minimal__section">
            <p className="about-minimal__eyebrow">04. Tooling</p>
            <h2 className="about-minimal__heading">Tooling</h2>
            <div className="about-minimal__tool-list">
              {TOOLING.map((tool) => (
                <span className="about-minimal__tool" key={tool}>
                  {tool}
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default About;
